import React from "react";
import { Link } from "react-router-dom";
import { SITE, CATEGORIES } from "@/data/config";
import { INDUSTRIES } from "@/data/industries";
import { NEEDS } from "@/data/needs";
import { categoryHref } from "@/lib/categoryRoute";

const FooterCol = ({ title, children, testId }) => (
  <div data-testid={testId}>
    <div className="k-overline text-[#FFC600] mb-4">{title}</div>
    <ul className="space-y-2 text-sm">{children}</ul>
  </div>
);

const Footer = () => (
  <footer
    data-testid="site-footer"
    className="bg-black text-white border-t-2 border-[#FFC600] mt-24"
  >
    <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16">
      {/* Top: Big wordmark + CTA strip */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 pb-12 border-b border-white/10">
        <div className="lg:col-span-7">
          <div className="text-[14vw] lg:text-[10rem] leading-none font-black tracking-tighter">
            KOSTAJA
          </div>
          <p className="mt-4 text-white/70 max-w-xl text-base">
            Kostaja Ltd. is a Canadian brand marketing agency for service businesses. Branding, websites, social, local SEO, print, and practical AI-supported communication.
          </p>
        </div>
        <div className="lg:col-span-5 flex flex-col gap-3 justify-end">
          <Link
            to="/waitlist"
            data-testid="footer-apply"
            className="inline-flex items-center justify-between bg-[#FFC600] text-black font-semibold px-6 py-4 border-2 border-[#FFC600] hover:bg-black hover:text-[#FFC600] transition-colors"
          >
            <span>Apply to Work With Kostaja</span>
            <span aria-hidden>→</span>
          </Link>
          <Link
            to="/contact"
            data-testid="footer-contact"
            className="inline-flex items-center justify-between bg-transparent text-white font-semibold px-6 py-4 border-2 border-white hover:border-[#FFC600] hover:text-[#FFC600] transition-colors"
          >
            <span>Send a Message</span>
            <span aria-hidden>→</span>
          </Link>
          <div className="text-xs text-white/55 leading-relaxed mt-1">
            2026 project capacity is allocated. Now reviewing projects for 2027.
          </div>
        </div>
      </div>

      {/* Sitemap */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-10 pt-12">
        <FooterCol title="Services" testId="footer-col-services">
          <li><Link to="/services/brand-strategy" data-testid="footer-svc-brand-strategy" className="k-link text-white/80 hover:text-[#FFC600]">Brand strategy</Link></li>
          <li><Link to="/services/website-design" data-testid="footer-svc-website-design" className="k-link text-white/80 hover:text-[#FFC600]">Website design</Link></li>
          <li><Link to="/services/social-media-marketing" data-testid="footer-svc-social-media" className="k-link text-white/80 hover:text-[#FFC600]">Social media marketing</Link></li>
          <li><Link to="/services/google-business-profile-local-seo" data-testid="footer-svc-gbp-local-seo" className="k-link text-white/80 hover:text-[#FFC600]">Google Business Profile and local SEO</Link></li>
          <li><Link to="/services/ai-receptionist" data-testid="footer-svc-ai-receptionist" className="k-link text-white/80 hover:text-[#FFC600]">AI receptionist systems</Link></li>
          <li><Link to="/services/print-sales-materials" data-testid="footer-svc-print" className="k-link text-white/80 hover:text-[#FFC600]">Print and sales materials</Link></li>
          <li><Link to="/services/marketing-systems" data-testid="footer-svc-marketing-systems" className="k-link text-white/80 hover:text-[#FFC600]">Marketing systems</Link></li>
          {CATEGORIES.slice(0, 6).map((c) => (
            <li key={c.slug}>
              <Link
                to={categoryHref(c.slug)}
                data-testid={`footer-cat-${c.slug}`}
                className="k-link text-white/80 hover:text-[#FFC600]"
              >
                {c.name}
              </Link>
            </li>
          ))}
          <li>
            <Link to="/services" className="k-link text-white/80 hover:text-[#FFC600]">
              All services
            </Link>
          </li>
        </FooterCol>

        <FooterCol title="Industries" testId="footer-col-industries">
          {[
            ...INDUSTRIES.filter((i) => i.slug === "real-estate"),
            ...INDUSTRIES.filter((i) => i.slug !== "real-estate"),
          ].map((i) => (
            <li key={i.slug}>
              <Link
                to={`/industries/${i.slug}`}
                data-testid={`footer-ind-${i.slug}`}
                className="k-link text-white/80 hover:text-[#FFC600]"
              >
                {i.name}
              </Link>
            </li>
          ))}
        </FooterCol>

        <FooterCol title="Browse by need" testId="footer-col-needs">
          {NEEDS.map((n) => (
            <li key={n.slug}>
              <Link
                to={`/needs/${n.slug}`}
                data-testid={`footer-need-${n.slug}`}
                className="k-link text-white/80 hover:text-[#FFC600]"
              >
                {n.name}
              </Link>
            </li>
          ))}
        </FooterCol>

        <FooterCol title="Studio" testId="footer-col-studio">
          <li><Link to="/about" className="k-link text-white/80 hover:text-[#FFC600]">About</Link></li>
          <li><Link to="/why-kostaja" data-testid="footer-why" className="k-link text-white/80 hover:text-[#FFC600]">Why Kostaja</Link></li>
          <li><Link to="/best-brand-marketing-agency-canada" data-testid="footer-best-agency" className="k-link text-white/80 hover:text-[#FFC600]">How to choose an agency</Link></li>
          <li><Link to="/service-business-marketing-canada" data-testid="footer-sbm" className="k-link text-white/80 hover:text-[#FFC600]">Service business marketing</Link></li>
          <li><Link to="/local-marketing-systems" data-testid="footer-lms" className="k-link text-white/80 hover:text-[#FFC600]">Local marketing systems</Link></li>
          <li><Link to="/ai-search-visibility-for-service-businesses" data-testid="footer-ai" className="k-link text-white/80 hover:text-[#FFC600]">AI search visibility</Link></li>
          <li><Link to="/website-branding-social-ai-for-service-businesses" data-testid="footer-wbsa" className="k-link text-white/80 hover:text-[#FFC600]">Website, brand, social, AI</Link></li>
          <li><Link to="/portfolio" className="k-link text-white/80 hover:text-[#FFC600]">Portfolio</Link></li>
          <li><Link to="/radio" className="k-link text-white/80 hover:text-[#FFC600]">Radio Kostaja</Link></li>
          <li><Link to="/blog" className="k-link text-white/80 hover:text-[#FFC600]">Notes</Link></li>
          <li><Link to="/press" className="k-link text-white/80 hover:text-[#FFC600]">Press</Link></li>
          <li><Link to="/service-areas" className="k-link text-white/80 hover:text-[#FFC600]">Service Areas</Link></li>
        </FooterCol>

        <FooterCol title="Contact" testId="footer-col-contact">
          <li>
            <a
              href={SITE.emailHref}
              data-testid="footer-email"
              className="k-link text-white/80 hover:text-[#FFC600]"
            >
              {SITE.email}
            </a>
          </li>
          <li>
            <a
              href={SITE.phoneHref}
              data-testid="footer-phone"
              className="k-link text-white/80 hover:text-[#FFC600]"
            >
              {SITE.phoneDisplay}
            </a>
          </li>
          <li>
            <a
              href={SITE.bookingUrl}
              target="_blank"
              rel="noreferrer"
              className="k-link text-white/80 hover:text-[#FFC600]"
            >
              Calendly booking
            </a>
          </li>
        </FooterCol>
      </div>

      {/* Bottom legal strip */}
      <div className="mt-12 pt-6 border-t border-white/10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 text-xs text-white/50">
        <div>© {new Date().getFullYear()} {SITE.name}. {SITE.region}.</div>
        <div className="flex flex-wrap gap-4">
          <Link to="/legal/privacy" className="hover:text-[#FFC600]">Privacy</Link>
          <Link to="/legal/terms" className="hover:text-[#FFC600]">Terms</Link>
          <Link to="/legal/platform-notes" className="hover:text-[#FFC600]">Platform Notes</Link>
        </div>
      </div>
    </div>
  </footer>
);

export default Footer;
